import { ADMIN_DETAILS } from "../../../../../../dataProvider/constant";
import moment from "moment";
import { getStorage } from "../../../../../../utils/common";

const toBooleanToggle = (value) => {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }

  return value === 1 || value === true;
};

const toNumericToggle = (value) => (toBooleanToggle(value) ? 1 : 0);

const serializeQliAreas = (qliAreas = {}) => {
  const rawValues = Array.isArray(qliAreas.value) ? qliAreas.value : [];

  return {
    toggle: toBooleanToggle(qliAreas.toggle),
    value: rawValues.map((item) => ({
      ...item,
      label: item && item.display_name ? item.display_name : "",
      value: item && item.id !== undefined ? item.id : "",
      isFixed: true
    }))
  };
};

const serializeAreas = (areas = {}) => {
  const rawValues = Array.isArray(areas.values) ? areas.values : [];

  return {
    toggle: toBooleanToggle(areas.toggle),
    values: rawValues.map((item, index) => {
      if (item && typeof item === "object" && item.label && item.value) {
        return item;
      }

      const areaText = `${item || ""}`;
      const stableKey = `${areaText}::${index}`;
      return {
        label: areaText,
        value: stableKey,
        swmAreaKey: stableKey
      };
    })
  };
};

const serializeAttachments = (attachments = {}) => {
  let files_path = "";
  try {
    let organisation = JSON.parse(getStorage(ADMIN_DETAILS)).organisation;
    let domain = new URL(organisation.logo_path).origin;
    let slug = organisation.slug;
    files_path = `${domain}/${slug}/`;
  } catch (e) {
    files_path = "";
  }

  const rawAttachments = Array.isArray(attachments.value) ? attachments.value.map(x => ({
    ...x,
    file_name: {
      ...x.file_name,
      isUploaded: true,
      file_base_path: files_path
    }
  })) : [];

  return {
    toggle: toBooleanToggle(attachments.toggle),
    value: rawAttachments
  }
}

const serializeScopeOfWork = (scopeOfWork = {}) => {
  const rawValue = scopeOfWork && scopeOfWork.value ? scopeOfWork.value : "";
  return {
    toggle: toBooleanToggle(scopeOfWork.toggle),
    value: rawValue
  }
}

const serializeDocumentProjectDetails = (data = {}) => {

  return {
    ...data,
    issue_date: data.issue_date ? moment.utc(data.issue_date, "YYYY-MM-DD") : null,
    qli_areas: serializeQliAreas(data.qli_areas),
    areas: serializeAreas(data.areas),
    attachments: serializeAttachments(data.attachments),
    scope_of_work: serializeScopeOfWork(data.scope_of_work)
  };
};

const serializeDocumentAuthorization = (data = {}) => {
  return {
    ...data,
    approvers: Array.isArray(data.approvers) ? data.approvers : [],
    toggle: toBooleanToggle(data.toggle),
    notes: data && data.notes !== undefined ? data.notes : "",
    noteToReviewers: data && data.noteToReviewers !== undefined ? data.noteToReviewers : null
  };
};

const serializeHighRiskActivities = (data = []) => {
  const categories = Array.isArray(data) ? data : [];

  return categories.map((category) => ({
    ...category,
    activities: Array.isArray(category && category.activities)
      ? category.activities.map((activity) => ({
        ...activity,
        selected: toBooleanToggle(activity && activity.selected)
      }))
      : []
  }));
};

const serializeMethodItemType = (data = {}) => {
  const methodItems = Array.isArray(data.method_items)
    ? data.method_items.map((item) => ({
      ...item,
      isNew: toBooleanToggle(item && (item.isNew || item.is_new)),
      selected: toBooleanToggle(item && item.selected)
    }))
    : [];

  const selected = methodItems
    .filter((item) => item.selected)
    .map((item) => ({
      ...item,
      label: item.method_item,
      value: item.id
    }));

  const enabled = {};
  methodItems.forEach((item) => {
    enabled[`item_${item.id}`] = toBooleanToggle(item.selected);
  });

  return {
    ...data,
    toggle: toBooleanToggle(data.toggle),
    method_items: methodItems,
    selected,
    enabled
  };
};

const serializeSwmsContentManagement = (data = {}) => {
  return {
    ...data,
    toggle: toBooleanToggle(data.toggle),
    description: data && data.description !== undefined ? data.description : ""
  };
};

const toDateString = (value) => {
  if (!value) {
    return null;
  }

  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

const serializeQliAreasForSubmit = (qliAreas = {}) => {
  const rawValues = Array.isArray(qliAreas.value) ? qliAreas.value : [];

  return {
    toggle: toNumericToggle(qliAreas.toggle),
    value: rawValues.map((item) => ({
      id: item && item.id !== undefined ? item.id : null,
      name: item && item.name ? item.name : "",
      display_name: item && item.display_name ? item.display_name : ""
    }))
  };
};

const serializeAreasForSubmit = (areas = {}) => {
  const rawValues = Array.isArray(areas.values) ? areas.values : [];

  return {
    toggle: toNumericToggle(areas.toggle),
    values: rawValues.map((item) => {
      if (item && typeof item === "object") {
        return item.label || item.value;
      }

      return item;
    })
  };
};

const serializeAttachmentsForSubmit = (attachments = {}) => {
  const rawAttachments = Array.isArray(attachments.value) ? attachments.value : [];

  return {
    toggle: toNumericToggle(attachments.toggle),
    value: rawAttachments.map((file) => {
      if (!file || !file.file_name || !file.file_name.file_name) {
        return null;
      }

      return {
        note: file.note,
        file_name: {
          file_name: file.file_name.file_name,
          visible_name: file.file_name.visible_name,
          type: file.file_name.type
        }
      };
    }).filter(Boolean)
  };
};

const serializeScopeOfWorkForSubmit = (scopeOfWork = {}) => {
  const rawValue = scopeOfWork && scopeOfWork.value ? scopeOfWork.value : "";
  return {
    toggle: toNumericToggle(scopeOfWork.toggle),
    value: rawValue
  };
}

const serializeDocumentProjectDetailsForSubmit = (data = {}) => {
  return {
    ...data,
    areas: serializeAreasForSubmit(data.areas),
    qli_areas: serializeQliAreasForSubmit(data.qli_areas),
    issue_date: toDateString(data.issue_date),
    attachments: serializeAttachmentsForSubmit(data.attachments),
    scope_of_work: serializeScopeOfWorkForSubmit(data.scope_of_work)
  };
};

const serializeDocumentAuthorizationForSubmit = (data = {}) => {
  const approvers = Array.isArray(data.approvers) ? data.approvers : [];

  return {
    ...data,
    approvers,
    toggle: toNumericToggle(data.toggle),
    notes: data && data.notes !== undefined ? data.notes : "",
    noteToReviewers: data && data.noteToReviewers !== undefined ? data.noteToReviewers : null
  };
};

const serializeHighRiskActivitiesForSubmit = (data = []) => {
  const categories = Array.isArray(data) ? data : [];

  return categories.map((category) => ({
    ...category,
    activities: Array.isArray(category && category.activities)
      ? category.activities.map((activity) => ({
        ...activity,
        selected: toNumericToggle(activity && activity.selected)
      }))
      : []
  }));
};

const serializeMethodItemTypeForSubmit = (data = {}) => {
  const items = Array.isArray(data.method_items) ? data.method_items : [];
  const enabled = data && data.enabled ? data.enabled : {};

  return {
    toggle: toNumericToggle(data.toggle),
    method_items: items
      .filter((item) => item && item.method_item)
      .map((item) => ({
        ...item,
        is_new: toNumericToggle(item && item.isNew),
        selected: toNumericToggle(enabled[`item_${item.id}`])
      }))
  };
};

const serializeSwmsContentManagementForSubmit = (data = {}) => {
  return {
    ...data,
    toggle: toNumericToggle(data.toggle),
    description: data && data.description !== undefined ? data.description : ""
  };
};

const serializeSectionDataForSubmit = (sectionKey, data) => {
  if (sectionKey === "document_project_details") {
    return serializeDocumentProjectDetailsForSubmit(data);
  }

  if (sectionKey === "document_authorization") {
    return serializeDocumentAuthorizationForSubmit(data);
  }

  if (sectionKey === "high_risk_activities") {
    return serializeHighRiskActivitiesForSubmit(data);
  }

  if (sectionKey.startsWith("method_item_type_")) {
    return serializeMethodItemTypeForSubmit(data);
  }

  if (sectionKey.startsWith("swms_content_management_")) {
    return serializeSwmsContentManagementForSubmit(data);
  }

  return data;
};

const mapVersionStatus = (status) => {
  const normalizedStatus = status ? `${status}`.trim().toUpperCase() : "IN_PROGRESS";
  if (normalizedStatus === "SUPERSEDED") return "superseded";
  if (normalizedStatus === "APPROVED") return "approved";
  return "in_progress";
};

const mapReviewerStatus = (status) => {
  const normalizedStatus = status ? `${status}`.trim().toUpperCase() : "PENDING";
  if (normalizedStatus === "APPROVED") return "approved";
  if (normalizedStatus === "REJECTED") return "rejected";
  if (normalizedStatus === "CANCELLED") return "cancelled";
  if (normalizedStatus === "NO_RESPONSE") return "no_response";
  return "pending";
};


export const mapApprovalSubmissionHistory = (approvalSubmissionHistory = [], swmsData = {}) => {
  if (!Array.isArray(approvalSubmissionHistory)) {
    return [];
  }

  const swmsId = swmsData && swmsData.sections && swmsData.sections.document_project_details && swmsData.sections.document_project_details.swms_number
    ? swmsData.sections.document_project_details.swms_number
    : null;

  return approvalSubmissionHistory.map((version) => ({
    version_number: version && version.versionNumber !== undefined && version.versionNumber !== null
      ? `v${version.versionNumber}`
      : null,
    versionNumber: version && version.versionNumber !== undefined && version.versionNumber !== null
      ? version.versionNumber
      : null,
    swms_id: swmsId,
    created_at: version && version.createdAt ? version.createdAt : null,
    created_by: version && version.createdByName ? version.createdByName : null,
    status: mapVersionStatus(version && version.status),
    submissions: Array.isArray(version && version.submissions)
      ? version.submissions.map((submission) => {
        const reviewerResponses = Array.isArray(submission && submission.reviewerResponses)
          ? submission.reviewerResponses
          : [];

        return {
          sent_at: submission && submission.submittedAt ? submission.submittedAt : null,
          is_cancelled: submission && submission.is_cancelled === true,
          reviewer_count: reviewerResponses.length,
          reviewers: reviewerResponses.map((reviewer) => ({
            name: reviewer && reviewer.reviewerName ? reviewer.reviewerName : (reviewer && reviewer.reviewerId ? reviewer.reviewerId : ""),
            position: reviewer && reviewer.role ? reviewer.role : (reviewer && reviewer.job_title ? reviewer.job_title : null),
            reviewerId: reviewer && reviewer.reviewerId ? reviewer.reviewerId : null,
            reviewerEmail: reviewer && reviewer.reviewerEmail
              ? reviewer.reviewerEmail
              : (reviewer && reviewer.email ? reviewer.email : (reviewer && reviewer.reviewer_email ? reviewer.reviewer_email : null)),
            status: mapReviewerStatus(reviewer && reviewer.status),
            responded_at: reviewer && reviewer.respondedAt ? reviewer.respondedAt : null,
            rejection_notes: reviewer && reviewer.rejectionNotes ? reviewer.rejectionNotes : null,
          })),
        };
      })
      : [],
  }));
};

export const serializeInitialValuesV3 = (swmsData = {}) => {
  const meta = swmsData && swmsData.meta && typeof swmsData.meta === "object"
    ? swmsData.meta
    : {};
  const sections = swmsData && swmsData.sections && typeof swmsData.sections === "object"
    ? swmsData.sections
    : {};

  const serializedSections = {
    ...sections
  };

  if (serializedSections.document_project_details) {
    serializedSections.document_project_details = {
      ...serializedSections.document_project_details,
      data: serializeDocumentProjectDetails(
        serializedSections.document_project_details.data
      )
    };
  }

  if (serializedSections.document_authorization) {
    serializedSections.document_authorization = {
      ...serializedSections.document_authorization,
      data: serializeDocumentAuthorization(
        serializedSections.document_authorization.data
      )
    };
  }

  if (serializedSections.high_risk_activities) {
    serializedSections.high_risk_activities = {
      ...serializedSections.high_risk_activities,
      data: serializeHighRiskActivities(
        serializedSections.high_risk_activities.data
      )
    };
  }

  Object.keys(serializedSections).forEach((sectionKey) => {
    if (sectionKey.startsWith("method_item_type_")) {
      serializedSections[sectionKey] = {
        ...serializedSections[sectionKey],
        data: serializeMethodItemType(serializedSections[sectionKey].data)
      };
    }
  });

  Object.keys(serializedSections).forEach((sectionKey) => {
    if (sectionKey.startsWith("swms_content_management_")) {
      serializedSections[sectionKey] = {
        ...serializedSections[sectionKey],
        data: serializeSwmsContentManagement(serializedSections[sectionKey].data)
      };
    }
  });

  return {
    meta,
    sections: serializedSections
  };
};

export const serializeSubmitValuesV3 = (formValues = {}, currentSwmsData = {}) => {
  const existingMeta = currentSwmsData && currentSwmsData.meta && typeof currentSwmsData.meta === "object"
    ? currentSwmsData.meta
    : {};
  const formMeta = formValues && formValues.meta && typeof formValues.meta === "object"
    ? formValues.meta
    : {};
  const existingSections = currentSwmsData && currentSwmsData.sections && typeof currentSwmsData.sections === "object"
    ? currentSwmsData.sections
    : {};
  const formSections = formValues && formValues.sections && typeof formValues.sections === "object"
    ? formValues.sections
    : {};

  const mergedSectionKeys = Array.from(new Set([
    ...Object.keys(existingSections),
    ...Object.keys(formSections)
  ]));

  const mergedMeta = {
    ...existingMeta,
    ...formMeta
  };

  const mergedSequence = mergedMeta && mergedMeta.sequence ? mergedMeta.sequence : {};
  const sequenceBuckets = ["fixed", "active", "inactive"];
  const orderedSequenceKeys = sequenceBuckets.flatMap((bucketName) => {
    const bucketItems = Array.isArray(mergedSequence[bucketName]) ? mergedSequence[bucketName] : [];
    return [...bucketItems]
      .sort((left, right) => Number(left && left.sequence_order ? left.sequence_order : 0) - Number(right && right.sequence_order ? right.sequence_order : 0))
      .map((item) => item && item.item_key)
      .filter((itemKey) => itemKey && mergedSectionKeys.includes(itemKey));
  });

  const fallbackSectionKeys = mergedSectionKeys
    .filter((sectionKey) => !orderedSequenceKeys.includes(sectionKey))
    .sort((leftKey, rightKey) => {
      const leftFormSection = formSections[leftKey] || {};
      const rightFormSection = formSections[rightKey] || {};
      const leftExistingSection = existingSections[leftKey] || {};
      const rightExistingSection = existingSections[rightKey] || {};

      const leftOrder = Number(
        leftFormSection.sequence_order !== undefined
          ? leftFormSection.sequence_order
          : leftExistingSection.sequence_order || 0
      );
      const rightOrder = Number(
        rightFormSection.sequence_order !== undefined
          ? rightFormSection.sequence_order
          : rightExistingSection.sequence_order || 0
      );

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      return leftKey.localeCompare(rightKey);
    });

  const sectionKeys = [
    ...orderedSequenceKeys,
    ...fallbackSectionKeys
  ];

  const serializedSections = {};

  sectionKeys.forEach((sectionKey) => {
    const existingSection = existingSections[sectionKey] || {};
    const formSection = formSections[sectionKey] || {};
    const resolvedData = formSection.data !== undefined
      ? formSection.data
      : existingSection.data;

    serializedSections[sectionKey] = {
      ...existingSection,
      ...formSection,
      data: serializeSectionDataForSubmit(sectionKey, resolvedData)
    };
  });

  return {
    meta: mergedMeta,
    sections: serializedSections
  };
};

export const serializeSubmitPayloadV3 = (formValues = {}, currentSwmsData = {}) => {
  return {
    ...currentSwmsData,
    ...serializeSubmitValuesV3(formValues, currentSwmsData)
  };
};
